import React, { useState, useEffect } from 'react';
import Button from './ui/Button';
import { examApi } from '../services/api';
import { FiBarChart2 } from 'react-icons/fi';
import './ExamHistoryList.css';
import { formatDateTime as formatDate } from '../utils/format';

interface ExamHistory {
  sessionId: string;
  examType: string;
  score: number;
  percentage: number;
  timeTakenMinutes: number;
  completedAt: string;
  passed: boolean;
}

interface ExamType {
  exam_type_id: string;
  name: string;
  icon?: string;
}

interface ExamHistoryListProps {
  history: ExamHistory[];
  onViewDetails: (sessionId: string) => void;
}

const ExamHistoryList: React.FC<ExamHistoryListProps> = ({ history, onViewDetails }) => {
  const [examTypes, setExamTypes] = useState<ExamType[]>([]);

  useEffect(() => {
    const loadExamTypes = async () => {
      try {
        const response = await examApi.getAllTypes();
        if (response.success && response.data) {
          setExamTypes(response.data as ExamType[]);
        }
      } catch (error) {
        console.error('Failed to load exam types:', error);
      }
    };

    loadExamTypes();
  }, []);

  const getExamTypeInfo = (examTypeId: string) => {
    return examTypes.find(type => type.exam_type_id === examTypeId);
  };

  const getExamTypeName = (examTypeId: string) => {
    const examType = getExamTypeInfo(examTypeId);
    return examType ? examType.name : examTypeId;
  };

  const formatTime = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return `${hours}시간 ${mins}분`;
    }
    return `${mins}분`;
  };

  if (history.length === 0) {
    return (
      <div className="exam-history-empty">
        <div className="history-list-empty">
          <div className="history-list-empty-icon"><FiBarChart2 aria-hidden="true" /></div>
          <h4>아직 완료한 시험이 없습니다</h4>
          <p>첫 번째 시험을 시작해보세요!</p>
        </div>
      </div>
    );
  }

  return (
    <div className="exam-history-list">
      <div className="history-list-table-wrap">
        <table className="history-list-table compact">
          <thead>
            <tr>
              <th className="col-exam">시험</th>
              <th className="col-code">코드</th>
              <th className="col-date">응시일</th>
              <th className="col-time">소요</th>
              <th className="col-score">점수</th>
              <th className="col-status">상태</th>
              <th className="col-action"></th>
            </tr>
          </thead>
          <tbody>
            {history.map((exam) => (
              <tr key={exam.sessionId}>
                <td className="col-exam">
                  <div className="history-list-table__exam">
                    {getExamTypeInfo(exam.examType)?.icon && (
                      <img src={getExamTypeInfo(exam.examType)!.icon} alt="" className="history-list-table__icon" />
                    )}
                    <span>{getExamTypeName(exam.examType)}</span>
                  </div>
                </td>
                <td className="col-code">
                  <code className="history-list-table__code">{exam.sessionId.slice(-8)}</code>
                </td>
                <td className="col-date">{formatDate(exam.completedAt)}</td>
                <td className="col-time">{formatTime(exam.timeTakenMinutes)}</td>
                <td className="col-score">
                  <strong className={`history-list-table__score ${exam.passed ? 'passed' : 'failed'}`}>
                    {exam.percentage}%
                  </strong>
                </td>
                <td className="col-status">
                  <span className={`history-list-badge ${exam.passed ? 'passed' : 'failed'}`}>
                    {exam.passed ? '합격' : '불합격'}
                  </span>
                </td>
                <td className="history-list-table__action col-action">
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={() => onViewDetails(exam.sessionId)}
                  >
                    상세
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ExamHistoryList;
